import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Paper, Alert } from '@mui/material';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment"; 

// Rutas y Servicios
import { api } from '../../controllers/ConsumeApiController';
import { stripePromisePK } from '../../apiRoutes/apiBase'; 
import WebsiteRoutes from '../../apiRoutes/WebsiteRoutes'; 
import { payMultipleOrders } from '../../controllers/customer/ClientPaymentController'; 

// Componentes Hijos (Tus formularios existentes)
import PaymentMethodTabs from './PaymentMethodTabs';
import StripePaymentForm from './forms/StripePaymentForm';
import BancoPlazaForm from './forms/BancoPlazaForm';
import CustomerRoutes from '../../apiRoutes/CustomerRoutes';

const stripePromise = loadStripe(stripePromisePK);

export default function MultiOrderPaymentContainer({ paymentData, merchantUuid, customer, onPaymentSuccess }) {
    // paymentData: { total_to_pay: 15.00, orders: [{id: 101}, {id: 102}] }
    // merchantUuid: El UUID de la Plataforma QUD (para saber qué bancos mostrar)

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Datos auxiliares
    const [exchangeRate, setExchangeRate] = useState(0);
    const [banksVe, setBanksVe] = useState([]);
    
    // 1. Cargar Métodos de Pago Disponibles (De la plataforma)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const url = WebsiteRoutes.Merchants.GetPaymentMethods + merchantUuid;
                const response = await api.get(url);
                
                if (response.success) {
                    setPaymentMethods(response.data.payment_methods);
                    setExchangeRate(parseFloat(response.data.exchange_rate));
                    setBanksVe(response.data.banks_ve || []);
                    
                    // Pre-seleccionar Stripe si existe
                    const stripeMethod = response.data.payment_methods.find(m => m.name === 'Stripe');
                    setSelectedMethod(stripeMethod || response.data.payment_methods[0]);
                }
            } catch (error) {
                console.error("Error loading methods", error);
            } finally {
                setIsLoadingData(false);
            }
        };
        fetchData();
    }, [merchantUuid]);

    /*
    |--------------------------------------------------------------------------
    | PROCESAR EL FLUJO: RECARGA -> PAGO AUTOMÁTICO
    |--------------------------------------------------------------------------
    */
    const handleProcessRechargeAndPay = async (paymentDataFromForm) => {
        setIsProcessing(true);

        try {
            if (!selectedMethod || !selectedMethod.accounts || selectedMethod.accounts.length === 0) {
                alert("Error: Configuración bancaria inválida.");
                setIsProcessing(false);
                return;
            }

            const bankAccountSelected = selectedMethod.accounts[0]; 
            const fecha = moment().format('YYYY-MM-DD');
            const amountToRecharge = parseFloat(paymentData.total_to_pay);

            // --- PASO A: PREPARAR RECARGA A LA WALLET ---
            // Usamos endpoint de Stripe/BancoPlaza existente para meter dinero a la cuenta
            let payload = {
                order_id: 0, // 0 indica recarga genérica
                amount: amountToRecharge,
                account_number_id: bankAccountSelected.id,
                bank_id: bankAccountSelected.bank_id,
                currency_id: 2, // 2 = USD (Ajustar según tu config)
                currency: 'USD',
                gateway_id: 'WEB',
                create_real_date: fecha,
                transaction_type_id: 1, // 1 = Depósito/Recarga
                customer: customer, 
                merchant_uuid: merchantUuid
            };

            if (paymentDataFromForm.type === 'stripe') {
                payload.provider = 'stripe';
                payload.payment_method_id = paymentDataFromForm.payment_method_id; 
            } 
            else if (paymentDataFromForm.type === 'pago_movil') {
                payload.provider = 'bancoplaza';
                payload.banco = paymentDataFromForm.bank;
                payload.telefonoCliente = paymentDataFromForm.phone;
                payload.referencia = paymentDataFromForm.reference;
                payload.amount_ves = (amountToRecharge * exchangeRate).toFixed(2);
            }

            // EJECUTAR RECARGA
            const urlRecarga = CustomerRoutes.Wallet.PaymentProcess;
            const responseRecarga = await api.post(urlRecarga, payload);

            if (responseRecarga.success) {
                
                // --- PASO B: EJECUTAR PAGO DE LOS PEDIDOS ---
                // El dinero ya está en la wallet, ahora lo usamos.
                
                // Extraemos IDs: [101, 102]
                const orderIds = paymentData.orders.map(o => o.id);
                
                try {
                    const responsePago = await payMultipleOrders(orderIds);

                    if (responsePago.success) {
                        onPaymentSuccess(true); // ¡Todo Éxito!
                    } else {
                        console.error("Fallo pago automático:", responsePago);
                        alert("Tu recarga fue exitosa, pero hubo un error aplicando el pago a los pedidos. Por favor intenta pagar nuevamente con tu saldo.");
                        onPaymentSuccess(true); // Redirigimos igual a éxito para que vea su saldo cargado
                    }
                } catch (payError) {
                    console.error(payError);
                    alert("Recarga exitosa. Error de conexión al finalizar pedidos.");
                    onPaymentSuccess(true);
                }

            } else {
                alert(responseRecarga.message || "Error al procesar el pago con el banco.");
            }

        } catch (error) {
            console.error(error);
            alert("Ocurrió un error de conexión.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoadingData) return <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>;

    return (
        <Paper elevation={0} sx={{ p: 0 }}>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Se realizará una recarga de <strong>${parseFloat(paymentData.total_to_pay).toFixed(2)}</strong> a tu Wallet y se pagarán los pedidos automáticamente.
            </Alert>

            <Typography variant="h6" align="center" gutterBottom sx={{ mb: 2 }}>
                Selecciona método de pago
            </Typography>

            <PaymentMethodTabs 
                methods={paymentMethods} 
                selected={selectedMethod} 
                onSelect={setSelectedMethod} 
            />

            <Box sx={{ mt: 3 }}>
                {selectedMethod?.name === 'Stripe' && (
                    <Elements stripe={stripePromise}>
                        <StripePaymentForm 
                            amount={paymentData.total_to_pay} 
                            onSubmit={handleProcessRechargeAndPay}
                            isLoading={isProcessing}
                            buttonText={`Pagar $${parseFloat(paymentData.total_to_pay).toFixed(2)}`}
                        />
                    </Elements>
                )}

                {selectedMethod?.name === 'Pago Movil' && (
                    <BancoPlazaForm 
                        amount={paymentData.total_to_pay}
                        exchangeRate={exchangeRate}
                        banksList={banksVe}
                        onSubmit={handleProcessRechargeAndPay}
                        isLoading={isProcessing}
                    />
                )}
            </Box>
        </Paper>
    );
}